import { useEffect, useMemo, useRef, useState } from "react";
import type { IChartApi, UTCTimestamp } from "lightweight-charts";
import { useOverview } from "../api/hooks/useOverview";
import { useBotInstances } from "../api/hooks/useBotInstances";
import { useTradeDetails } from "../api/hooks/useTrades";
import { KpiCard } from "../components/KpiCard/KpiCard";
import { PriceChart, type ChartMode, type PriceChartCandle, type PriceChartLevel, type PriceChartMarker } from "../components/PriceChart/PriceChart";
import { PnlChart, type PnlBarPoint } from "../components/PnlChart/PnlChart";
import { TradeDetailPanel } from "../components/TradeDetailPanel/TradeDetailPanel";
import { TooltipBubble } from "../components/HoverTip/HoverTip";
import { useTheme } from "../theme/useTheme";
import { useNavigate } from "react-router-dom";
import type { OpenPosition } from "../api/types";
import { PERIOD_TABS, canStepForward, computeWindow, includesToday, type PeriodType } from "../utils/period";
import { formatDate, formatMoney, formatPnl, formatPnlMoney, formatTime, formatTradeId } from "../utils/format";

const CHART_MODES: { key: ChartMode; label: string }[] = [
  { key: "line", label: "Линия" },
  { key: "candles", label: "Свечи" },
];

export function Overview() {
  // Replaced the old 1д/7д/30д/всё range enum + scroll-triggered "load more
  // history" pagination — see utils/period.ts's own comment for the two
  // window families (rolling Nд/12м vs. calendar month/quarter/year) and
  // why offset alone (not a per-periodType anchor) is enough state. Default
  // "d7" mirrors the old default's intent ("recent activity at a glance")
  // now that 1д itself is gone from the tab set.
  const [periodType, setPeriodType] = useState<PeriodType>("d7");
  const [offset, setOffset] = useState(0);
  const [mode, setMode] = useState<ChartMode>("line");
  const [showTradeLevels, setShowTradeLevels] = useState(false);
  // "all" = aggregate across every bot instance (default) — see
  // API_CONTRACT.md's bot_instance_id param. Any other value scopes
  // kpi/open_positions/trade_markers/recent_trades/bot_status to just that
  // one instance; price_series/candles stay unfiltered regardless (shared
  // market price, not bot-specific — see overview.py's own comment).
  const [botFilter, setBotFilter] = useState<string>("all");
  const { data: botInstances } = useBotInstances();
  // Computed once per periodType/offset change (Date.now() is NOT a
  // useMemo dep) — recomputing on every render would mint a new [from,to)
  // — and therefore a new query key/refetch — practically every render.
  // The window stays fixed between polls; it only moves when the user
  // picks a different tab or clicks a nav arrow.
  const window_ = useMemo(() => computeWindow(periodType, offset, Date.now()), [periodType, offset]);
  const fromIso = useMemo(() => window_.from.toISOString(), [window_]);
  const toIso = useMemo(() => window_.to.toISOString(), [window_]);
  const { data, isLoading } = useOverview(fromIso, toIso, botFilter === "all" ? undefined : botFilter);
  const [theme] = useTheme();
  const navigate = useNavigate();

  // MSK date range shown under the period tabs — every period type is now
  // block-aligned to a whole MSK calendar day or larger unit (see
  // computeWindow: d3/d7/d30 became full-day blocks per user request, not
  // exact-instant rolling windows), so a date-only "DD.MM.YYYY" is always
  // enough, no time-of-day needed anywhere. `to` is an EXCLUSIVE bound —
  // displayed minus 1s so "прошлый месяц" reads as ending 31.07.2026, not
  // 01.08.2026.
  const windowLabel = useMemo(() => {
    const displayEnd = new Date(window_.to.getTime() - 1000);
    return `${formatDate(window_.from.toISOString())} – ${formatDate(displayEnd.toISOString())} МСК`;
  }, [window_]);

  // Two-way timeScale sync, price chart <-> P&L-по-сделкам panel below it
  // (design reference: they're one visual unit, stacked and sharing an
  // x-axis — panning/zooming EITHER one, including mouse-wheel/pinch
  // zoom, moves both). lightweight-charts v4 has no built-in multi-pane
  // layout (that's a v5 feature) — two separate chart instances kept in
  // lockstep is the standard workaround.
  //
  // Two-way sync is gated by syncReadyRef, NOT unconditional — confirmed
  // live that an unconditional two-way subscription actively corrupts
  // things during data load: a series' first setData() ever on a chart
  // auto-resets that chart's own visible range (a lightweight-charts
  // internal behavior, not something call-site code triggers or can
  // suppress), and the P&L panel's own auto-reset-on-first-data
  // immediately propagated backward and overwrote the price chart's own
  // correctly-fitted "всё" range with the P&L panel's own narrow one —
  // confirmed by console-logging every range-change event live. Fix:
  // handlePnlChartApi marks sync NOT ready the instant a fresh PnlChart
  // is created (before its data-effect's setData() call, which is what
  // triggers the internal auto-reset) — that suppresses exactly the
  // corrupting event — and the post-mount resync effect further down
  // marks it ready again only after establishing the correct synced
  // state. From then on, genuine user-driven pan/zoom on either chart
  // (which only ever happens after mount, never mid-setData) propagates
  // normally both ways.
  //
  // Synced by TIME (getVisibleRange/setVisibleRange), not by logical
  // index (getVisibleLogicalRange/setVisibleLogicalRange) — confirmed live
  // this actually matters here, not just a style choice: the price chart
  // has ~3600 hourly points and the P&L chart has ~65 trade bars, so
  // "logical index 3532" means a completely different moment in time on
  // each chart. Syncing logical ranges asked the P&L chart to show
  // "bars 3532–3647", which mostly don't exist on a 65-bar series — it
  // rendered almost empty with 1-2 bars squeezed into a corner. Time-based
  // sync has no such mismatch: a given timestamp means the same instant on
  // both charts regardless of how densely each one samples it.
  const mainChartRef = useRef<IChartApi | null>(null);
  const pnlChartRef = useRef<IChartApi | null>(null);
  const syncReadyRef = useRef(false);
  // Which chart's NEXT visibleTimeRangeChange event is purely an echo of a
  // setVisibleRange() call syncRange itself just made — not real user
  // panning. Without this, a feedback loop was confirmed live: user pans
  // the MAIN chart -> syncRange(main, pnl) sets pnl's range to match, but
  // pnl has far fewer bars than main (trades vs. hourly price points), so
  // it can legitimately only show a NARROWER achievable range -> pnl's own
  // subscribeVisibleTimeRangeChange fires with that narrower, CLAMPED
  // range -> syncRange(pnl, main) sees it differs from main's actual
  // current range by more than tolerance and reflects it straight back
  // onto main, silently overwriting the user's own pan with pnl's clamped
  // one. Confirmed live via console logging: the main chart's logical
  // range oscillated between exactly two fixed values every scroll tick
  // and never advanced — this is also why "load more history" stopped
  // re-triggering after one successful page, since the visible range could
  // never actually reach the near-left-edge trigger threshold.
  // syncRange sets this to `to` right before calling `to`'s
  // setVisibleRange(); each chart's change handler checks it against
  // itself first and, if set, consumes it (clears + returns) instead of
  // treating the echo as a fresh user-driven range to re-sync outward.
  const suppressSyncRef = useRef<IChartApi | null>(null);

  // Every timeScale() call below is wrapped in one try/catch, covering
  // both real failure modes confirmed live:
  //   - setVisibleRange() throws (not returns null/false) when the target
  //     chart's series has zero data points — e.g. the default "1д" range
  //     can legitimately have no CLOSED trades yet (today's only trade
  //     still open), so pnlBars is empty and PnlChart's series has
  //     nothing in it.
  //   - ANY call throws "Object is disposed" if it lands on a chart mid
  //     teardown — a range switch unmounts+remounts both charts (see the
  //     isLoading early-return above), and a subscription callback
  //     registered on the chart being torn down can still fire once more
  //     during that window. getVisibleRange() is just as exposed to this
  //     as setVisibleRange() was, so guarding only the setter (an earlier
  //     version of this code) still let the page blank out on the getter.
  // Both are "nothing to sync right now", not bugs each time they happen,
  // so this swallows rather than adding "does this chart/series still
  // exist" pre-checks lightweight-charts has no public API for anyway.
  const syncRange = (from: IChartApi, to: IChartApi) => {
    if (suppressSyncRef.current === from) {
      // This IS the echo syncRange(to, from) scheduled a moment ago — see
      // suppressSyncRef's own comment. Consume it and stop right here,
      // rather than treating `from`'s (possibly clamped) range as a new
      // user-driven change to propagate right back onto `to`.
      suppressSyncRef.current = null;
      return;
    }
    try {
      const fromRange = from.timeScale().getVisibleRange();
      if (!fromRange) return;
      const f = fromRange.from as number;
      const t = fromRange.to as number;
      const toRange = to.timeScale().getVisibleRange();
      if (toRange) {
        // Tolerance, not exact equality — confirmed live this is what
        // actually matters, not a style choice: setVisibleRange() snaps
        // internally to bar boundaries and does NOT guarantee
        // getVisibleRange() afterward returns back the exact range that
        // was requested. With exact equality, that trivial snap
        // difference meant the "already synced, skip" short-circuit
        // below NEVER fired — every sync round retriggered the other
        // chart's own subscription, forever, and the visible range
        // visibly ballooned wider each iteration. Reported live as "the
        // chart keeps drifting/zooming out on its own while hovering,
        // and hovering a tooltip on/off just makes it worse" — confirmed
        // by screenshotting the SAME stationary hover 500ms apart: the
        // visible price range and time ticks had already changed
        // significantly with zero mouse movement in between.
        const tf = toRange.from as number;
        const tt = toRange.to as number;
        const tolerance = Math.max(1, (t - f) * 0.005);
        if (Math.abs(tf - f) < tolerance && Math.abs(tt - t) < tolerance) return;
      }
      suppressSyncRef.current = to;
      to.timeScale().setVisibleRange(fromRange);
    } catch {
      // setVisibleRange (or a getter above) threw — no change event will
      // fire on `to` for us to consume, so an armed flag would otherwise
      // sit there and wrongly swallow the NEXT real event on `to`.
      suppressSyncRef.current = null;
    }
  };

  const handleMainChartApi = (chart: IChartApi) => {
    mainChartRef.current = chart;
    // Same reasoning as handlePnlChartApi's own syncReadyRef=false below —
    // this chart gets a BRAND NEW IChartApi (and calls onChartApi again)
    // not just on mount but on every Линия<->Свечи mode switch too (mode
    // recreates the whole chart, see PriceChart.tsx's create effect) —
    // PnlChart does NOT get recreated by that same switch, so it's still
    // showing whatever range it had before. Without suppressing here, that
    // STALE pnl range wins the race against this new chart's own
    // fitContent(): confirmed live via direct getVisibleLogicalRange()
    // logging that fitContent() itself computed the correct ~full-dataset
    // range every time, but the RENDERED chart still ended up showing a
    // narrow leftover window — pnlChart's sync subscription (never
    // suppressed for a pure mode switch, only ever reset by
    // handlePnlChartApi) pushed its own unrelated old range onto this
    // chart shortly after. The resync effect below (now also keyed on
    // `mode`) flips this back to true once this chart's own fitContent has
    // actually run.
    syncReadyRef.current = false;
    chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      if (pnlChartRef.current) syncRange(chart, pnlChartRef.current);
    });
  };

  const handlePnlChartApi = (chart: IChartApi) => {
    pnlChartRef.current = chart;
    // See the block comment above — must be false before this chart's own
    // data-effect (which hasn't run yet) calls setData() and triggers its
    // internal auto-reset; the post-mount resync effect flips it back on.
    syncReadyRef.current = false;
    chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      if (!syncReadyRef.current) return;
      if (mainChartRef.current) syncRange(chart, mainChartRef.current);
    });
  };

  // lightweight-charts' line/area series require STRICTLY ascending `time`
  // — collapsing sub-second timestamps to whole seconds (the chart's own
  // display granularity) can tie two DIFFERENT bots' near-simultaneous
  // price samples onto the same second now that market_metrics interleaves
  // several bot instances' rows (multi-bot support). A tie (never an
  // outright reversal — floor() of an already-ascending sequence can only
  // tie, not go backward) silently breaks the WHOLE series: setData()
  // accepts it without throwing, but nothing paints — confirmed live, "посл.
  // 7д" + "Линия" was 100% blank (317 points, 8 tied after flooring) while
  // "Свечи" (bucketed via date_trunc, collision-proof by construction)
  // rendered fine on the exact same window. This is also almost certainly
  // what surfaced as the uncaught "Value is null" inside lightweight-charts'
  // own requestAnimationFrame paint (see main.tsx) — a real data bug, not
  // the harmless chart-teardown race that error message usually is.
  const chartData = useMemo(() => {
    const points = (data?.price_series ?? []).map((p) => ({
      time: Math.floor(new Date(p.ts).getTime() / 1000) as UTCTimestamp,
      value: parseFloat(p.price),
    }));
    const deduped: typeof points = [];
    for (const p of points) {
      if (deduped.length > 0 && p.time <= deduped[deduped.length - 1].time) {
        deduped[deduped.length - 1] = p; // last sample for that second wins
      } else {
        deduped.push(p);
      }
    }
    return deduped;
  }, [data?.price_series]);

  // Bucketed OHLC bars — see overview.py's _load_candles docstring: built
  // from the SAME market_metrics price samples chartData uses, not a
  // separate feed, just grouped into buckets instead of shown as individual
  // points.
  const chartCandles = useMemo<PriceChartCandle[]>(
    () =>
      (data?.candles ?? []).map((c) => ({
        time: Math.floor(new Date(c.ts).getTime() / 1000) as UTCTimestamp,
        open: parseFloat(c.open),
        high: parseFloat(c.high),
        low: parseFloat(c.low),
        close: parseFloat(c.close),
      })),
    [data?.candles],
  );

  // EVERY currently open position, not just one — with several bot
  // instances trading at once, more than one can legitimately be open
  // simultaneously (confirmed live: 3 bots each opened their own). See
  // OpenPositionCard below for the per-position layout.
  const positions = data?.open_positions ?? [];

  // Every trade in the selected window — not just the open position — so
  // the chart shows the bot's full history of entries/exits, hoverable for
  // details and clickable to jump to that trade on the Сделки page.
  const markers = useMemo<PriceChartMarker[]>(
    () =>
      (data?.trade_markers ?? []).map((m) => ({
        time: Math.floor(new Date(m.ts).getTime() / 1000) as UTCTimestamp,
        tradeId: m.trade_id,
        botLabel: m.bot_label,
        kind: m.kind,
        direction: m.direction,
        price: parseFloat(m.price),
        entryPrice: parseFloat(m.entry_price),
        exitPrice: m.exit_price !== null ? parseFloat(m.exit_price) : null,
        entryTime: Math.floor(new Date(m.entry_time).getTime() / 1000) as UTCTimestamp,
        exitTime: m.exit_time !== null ? (Math.floor(new Date(m.exit_time).getTime() / 1000) as UTCTimestamp) : null,
        size: m.size,
        pnl: m.pnl,
        status: m.status,
        stopLoss: m.stop_loss !== null ? parseFloat(m.stop_loss) : null,
        takeProfit: m.take_profit !== null ? parseFloat(m.take_profit) : null,
      })),
    [data?.trade_markers],
  );

  // SL/TP for every trade currently on the chart (not just the open
  // position) — opt-in via a toggle since a wide range can hold many
  // trades and every SL/TP is drawn as a full-width horizontal line
  // (lightweight-charts price lines aren't scoped to a time segment), which
  // gets busy fast. One line per trade per side, deduped by trade_id since
  // each trade contributes up to two markers (entry+exit) carrying the same
  // stop_loss/take_profit.
  const tradeLevels = useMemo<PriceChartLevel[]>(() => {
    if (!showTradeLevels) return [];
    const seen = new Set<string>();
    const lv: PriceChartLevel[] = [];
    for (const m of markers) {
      if (seen.has(m.tradeId)) continue;
      seen.add(m.tradeId);
      // 0 shows up in the data as "not actually set" on some trades (not
      // NULL) — same as the open-position panel's existing take_profit
      // check below, guard on truthy, not just non-null, or the chart gets
      // a bogus price line pinned at 0.
      const label = formatTradeId(m.tradeId);
      if (m.stopLoss) lv.push({ price: m.stopLoss, kind: "sl", title: `SL ${label}` });
      if (m.takeProfit) lv.push({ price: m.takeProfit, kind: "tp", title: `TP ${label}` });
    }
    return lv;
  }, [markers, showTradeLevels]);

  // One bar per CLOSED trade, at its close time — exit markers only (an
  // OPEN position's entry marker has pnl:null, filtered out below), same
  // trades the price chart's own "выход" arrows show above.
  const pnlBars = useMemo<PnlBarPoint[]>(
    () =>
      markers
        .filter((m) => m.kind === "exit" && m.pnl !== null)
        .map((m) => ({ time: m.time, tradeId: m.tradeId, pnl: parseFloat(m.pnl as string) })),
    [markers],
  );

  // This window's own net P&L (sum of the bars currently loaded) — not the
  // KPI card's all-time Общий P&L, which the header explicitly disclaims
  // via "накопительно" scoped to what's actually on screen.
  const pnlNet = useMemo(() => pnlBars.reduce((sum, b) => sum + b.pnl, 0), [pnlBars]);

  // Same closed-trade markers pnlBars/pnlNet already reduce, kept as full
  // objects and sorted newest-first — powers "Сделки за период" below.
  // Replaces the old "latest 5 trades globally" recent_trades widget (user
  // request): this list must track the SAME window the chart/P&L above it
  // are scoped to, not a fixed count independent of whatever period is on
  // screen. `m.time` for an exit-kind marker IS the trade's close time (see
  // overview.py's _load_trade_markers: kind="exit" sets ts=close_time) —
  // no separate close-time field needed.
  //
  // Capped to the 10 most recent (user request, 2026-08-19) — a wide window
  // (12м/всё) could otherwise hand the sidebar hundreds of rows; "все →"
  // right above already links to the full uncapped list on /trades.
  const periodTrades = useMemo<PriceChartMarker[]>(
    () =>
      markers
        .filter((m) => m.kind === "exit" && m.pnl !== null)
        .slice()
        .sort((a, b) => b.time - a.time)
        .slice(0, 10),
    [markers],
  );

  // "Открытая позиция" widget only makes sense when the selected window
  // actually reaches today — user request: paged into "прошлый месяц", a
  // position that's still open isn't "open in July", it just hasn't closed
  // YET (a fact about now). See includesToday's own comment for why this
  // isn't simply `window_.to >= now`.
  const showOpenPositions = includesToday(window_, Date.now());

  // Re-sync after every data reload (range switch, or the initial mount)
  // instead of relying solely on the live pan/zoom subscriptions above,
  // AND flip syncReadyRef back on afterward (see the block comment near
  // handlePnlChartApi for why it was turned off). Three reasons the resync
  // itself is needed, all confirmed live: (1) on mount, PnlChart doesn't
  // exist yet when PriceChart's own fitContent() first fires, so that
  // particular range change has nowhere to propagate to; (2) a chart
  // series' first setData() call can itself silently reset that chart's
  // own visible range (seen with PnlChart specifically), *after* any sync
  // that already ran; (3) `mode` (Линия/Свечи) is in this effect's own deps
  // — a pure mode switch recreates ONLY the price chart (see
  // handleMainChartApi's own comment on why it sets syncReadyRef false),
  // and doesn't touch chartData/pnlBars at all, so without `mode` here
  // this effect would never rerun to flip syncReadyRef back on and the
  // price chart would stay permanently desynced from pnl after the very
  // first mode switch. Effects run child-before-parent, so this — the
  // parent's own effect — always runs after both charts' internal effects
  // (including PriceChart's own now-synchronous fitContent) have already
  // fired for this render, and wins over all three cases by running last.
  useEffect(() => {
    if (mainChartRef.current && pnlChartRef.current) {
      syncRange(mainChartRef.current, pnlChartRef.current);
    }
    syncReadyRef.current = true;
  }, [chartData, pnlBars, mode]);

  const levels = useMemo<PriceChartLevel[]>(() => {
    const lv: PriceChartLevel[] = [];
    // Bot label suffix only when there's more than one position to tell
    // apart — with just one (the common case, one bot trading) the plain
    // "вход"/"SL"/"TP" labels already used before multi-bot support stay
    // exactly as they were, no added clutter.
    for (const pos of positions) {
      const suffix = positions.length > 1 && pos.bot_label ? ` ${pos.bot_label}` : "";
      lv.push({ price: parseFloat(pos.entry_price), kind: "entry", title: `вход${suffix}` });
      lv.push({ price: parseFloat(pos.stop_loss), kind: "sl", title: `SL${suffix}` });
      if (pos.take_profit) lv.push({ price: parseFloat(pos.take_profit), kind: "tp", title: `TP${suffix}` });
    }
    return [...lv, ...tradeLevels];
  }, [positions, tradeLevels]);

  if (isLoading || !data) {
    return (
      <div style={{ padding: "26px 28px 44px" }}>
        <span style={{ font: "400 13px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Загрузка…</span>
      </div>
    );
  }

  // Per-bot breakdown for "P&L за период"'s own tooltip — built from the
  // SAME window-scoped markers pnlNet itself sums (not kpi.balances_by_bot,
  // which is all-time), so the tooltip always agrees with the headline
  // number it explains. Only shown with more than one bot contributing a
  // closed trade in this window.
  const pnlByBot = new Map<string, number>();
  for (const m of markers) {
    if (m.kind !== "exit" || m.pnl === null) continue;
    const key = m.botLabel ?? "—";
    pnlByBot.set(key, (pnlByBot.get(key) ?? 0) + parseFloat(m.pnl));
  }
  const pnlBreakdown =
    pnlByBot.size > 1 ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {[...pnlByBot.entries()].map(([botLabel, pnl]) => (
          <div key={botLabel} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
            <span style={{ color: "var(--fg2)" }}>{botLabel}</span>
            <span style={{ color: pnl >= 0 ? "var(--long)" : "var(--short)" }}>{formatPnl(pnl)}</span>
          </div>
        ))}
      </div>
    ) : undefined;

  return (
    <div style={{ padding: "26px 28px 44px", display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Period tabs + bot filter, above every widget — user request: the
          old 1д/7д/30д/всё switcher lived inside the chart card's own
          header, but it (like the bot filter already up here) affects
          things well outside the chart too — the P&L widget below is now
          window-scoped by it too, not just the chart. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 11, background: "var(--surface2)", flexWrap: "wrap" }}>
          {PERIOD_TABS.map((t) => {
            const active = t.periodType === periodType && t.initialOffset === offset;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setPeriodType(t.periodType);
                  setOffset(t.initialOffset);
                }}
                style={{
                  padding: "5px 11px",
                  border: 0,
                  borderRadius: 9,
                  cursor: "pointer",
                  font: "600 11.5px 'JetBrains Mono',monospace",
                  background: active ? "var(--accent)" : "transparent",
                  color: active ? "var(--onfill)" : "var(--fg2)",
                  whiteSpace: "nowrap",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Only rendered once there's actually more than one bot instance
            registered — a single-bot deployment (still the common case)
            gets no extra control at all. */}
        {botInstances && botInstances.instances.length > 1 && (
          <select
            value={botFilter}
            onChange={(e) => setBotFilter(e.target.value)}
            title="Фильтр по боту — влияет на KPI, статус, открытые позиции, последние сделки и график; цена BTC (общий рынок) не фильтруется"
            style={{
              padding: "6px 11px",
              border: "1px solid var(--line)",
              borderRadius: 10,
              cursor: "pointer",
              font: "600 12px 'JetBrains Mono',monospace",
              background: "var(--surface2)",
              color: "var(--fg2)",
            }}
          >
            <option value="all">Все боты</option>
            {botInstances.instances.map((b) => (
              <option key={b.bot_instance_id} value={b.bot_instance_id}>
                {b.display_name ?? b.bot_instance_id}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Selected window's exact MSK date/time range + prev/next nav —
          arrows jump by the tab's own span (7д tab -> ±7 days), replacing
          the old scroll-to-load-more-history gesture entirely (see
          PriceChart's disablePan prop below). Disabled for "всё" — there's
          no adjacent window to page into, the tab already shows everything. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
        <button
          type="button"
          onClick={() => setOffset((o) => o - 1)}
          disabled={periodType === "all"}
          title="Предыдущий период"
          style={{
            width: 30,
            height: 30,
            border: "1px solid var(--line)",
            borderRadius: 9,
            background: "transparent",
            color: periodType === "all" ? "var(--fg3)" : "var(--fg2)",
            cursor: periodType === "all" ? "default" : "pointer",
            opacity: periodType === "all" ? 0.4 : 1,
            font: "600 14px 'JetBrains Mono',monospace",
          }}
        >
          ←
        </button>
        <span style={{ font: "400 12px 'JetBrains Mono',monospace", color: "var(--fg2)", minWidth: 200, textAlign: "center" }}>{windowLabel}</span>
        <button
          type="button"
          onClick={() => setOffset((o) => o + 1)}
          disabled={periodType === "all" || !canStepForward(offset)}
          title="Следующий период"
          style={{
            width: 30,
            height: 30,
            border: "1px solid var(--line)",
            borderRadius: 9,
            background: "transparent",
            color: periodType === "all" || !canStepForward(offset) ? "var(--fg3)" : "var(--fg2)",
            cursor: periodType === "all" || !canStepForward(offset) ? "default" : "pointer",
            opacity: periodType === "all" || !canStepForward(offset) ? 0.4 : 1,
            font: "600 14px 'JetBrains Mono',monospace",
          }}
        >
          →
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start" }}>
        {/* overflow: visible (not hidden, unlike the other Overview cards) —
            the chart's hover tooltip (PriceChart.tsx) is position:absolute
            inside this section and shifts up via translate(-50%,-120%);
            near the top/edges of the 500px-tall chart it can extend past
            this card's own box. The rounded corners don't actually need
            hidden overflow to look right: the chart canvas is already well
            inset by its own padding + the header/legend rows above it, it
            never reaches this section's edge. */}
        <section style={{ flex: "1 1 620px", minWidth: 0, borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "15px 18px", borderBottom: "1px solid var(--line)" }}>
            <span style={{ font: "600 14.5px 'Golos Text',sans-serif" }}>Цена и решения бота</span>
            <div style={{ flex: 1, minWidth: 8 }} />
            <button
              type="button"
              onClick={() => setShowTradeLevels((v) => !v)}
              title="Показать/скрыть стоп-лосс и тейк-профит по всем сделкам, видимым сейчас на графике"
              style={{
                padding: "5px 11px",
                border: "1px solid var(--line)",
                borderRadius: 9,
                cursor: "pointer",
                font: "600 11.5px 'JetBrains Mono',monospace",
                background: showTradeLevels ? "var(--accent)" : "transparent",
                color: showTradeLevels ? "var(--onfill)" : "var(--fg2)",
              }}
            >
              SL/TP по сделкам
            </button>
            <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 11, background: "var(--surface2)" }}>
              {CHART_MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  style={{
                    padding: "5px 11px",
                    border: 0,
                    borderRadius: 9,
                    cursor: "pointer",
                    font: "600 11.5px 'JetBrains Mono',monospace",
                    background: mode === m.key ? "var(--accent)" : "transparent",
                    color: mode === m.key ? "var(--onfill)" : "var(--fg2)",
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", padding: "10px 18px 4px", font: "400 11.5px 'Golos Text',sans-serif", color: "var(--fg2)" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 15, height: 2, background: "var(--accent)", borderRadius: 2 }} />
              цена
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 15, height: 0, borderTop: "2px dashed var(--short)" }} />
              стоп-лосс
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 15, height: 0, borderTop: "2px dashed var(--long)" }} />
              тейк-профит
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderBottom: "7px solid var(--long)" }} />
              вход/выход
            </span>
          </div>

          <div style={{ padding: "4px 8px 10px" }}>
            <PriceChart
              mode={mode}
              data={chartData}
              candles={chartCandles}
              markers={markers}
              levels={levels}
              theme={theme}
              height={500}
              disablePan
              onMarkerClick={(tradeId) => navigate(`/trades?highlight=${encodeURIComponent(tradeId)}`)}
              onChartApi={handleMainChartApi}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px 2px 18px" }}>
            <span style={{ font: "600 10.5px 'Golos Text',sans-serif", letterSpacing: ".05em", color: "var(--fg3)", textTransform: "uppercase" }}>
              P&amp;L по сделкам
            </span>
            <span style={{ font: "400 10.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>
              накопительно{" "}
              <span style={{ color: pnlNet >= 0 ? "var(--long)" : "var(--short)", fontWeight: 600 }}>{formatPnl(pnlNet)}</span>
            </span>
          </div>
          <div style={{ padding: "0 8px 6px" }}>
            <PnlChart
              data={pnlBars}
              theme={theme}
              height={140}
              disablePan
              onMarkerClick={(tradeId) => navigate(`/trades?highlight=${encodeURIComponent(tradeId)}`)}
              onChartApi={handlePnlChartApi}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 16px 12px", font: "400 11px 'JetBrains Mono',monospace", color: "var(--fg3)", borderTop: "1px solid var(--line)" }}>
            <span>точек цены {chartData.length.toLocaleString("ru-RU")}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>сделок {pnlBars.length.toLocaleString("ru-RU")}</span>
            <div style={{ flex: 1, minWidth: 8 }} />
            {/* Deliberately quiet — same muted footer style/size as the
                points/trades counters to the left, not a call-to-action. */}
            <a href="https://app.hyperliquid.xyz/trade/BTC" target="_blank" rel="noopener noreferrer" style={{ color: "var(--fg3)", opacity: 0.7 }}>
              Hyperliquid
            </a>
            <a href="https://www.tradingview.com/symbols/BTCUSDC/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--fg3)", opacity: 0.7 }}>
              TradingView
            </a>
            <a href="https://www.coingecko.com/en/coins/bitcoin" target="_blank" rel="noopener noreferrer" style={{ color: "var(--fg3)", opacity: 0.7 }}>
              CoinGecko
            </a>
          </div>
        </section>

        <div style={{ flex: "1 1 320px", maxWidth: 380, minWidth: 0, display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Same sidebar as Открытая позиция/Последние сделки — user
              request: this widget was its own top-level row above the
              chart, moved down here alongside the other per-window/
              per-account widgets instead. */}
          <KpiCard
            label="P&L за период"
            value={formatPnlMoney(pnlNet)}
            valueColor={pnlNet >= 0 ? "var(--long)" : "var(--short)"}
            tooltip={pnlBreakdown}
            suffix={
              <span style={{ font: "500 11.5px 'Golos Text',sans-serif", color: "var(--fg3)", paddingBottom: 6 }}>
                {pnlBars.length.toLocaleString("ru-RU")} сделок
              </span>
            }
          />
          {/* Only rendered when the selected window reaches today — see
              showOpenPositions' own comment. Paged into a past period this
              disappears entirely rather than showing a "Нет открытой
              позиции" placeholder that would otherwise misread as "the bot
              had nothing open back then" (unknowable from this window's
              own data — open_positions is always "right now", never
              historical, see overview.py). */}
          {showOpenPositions && (
            <section style={{ borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "15px 18px", borderBottom: "1px solid var(--line)" }}>
                <span style={{ font: "600 14.5px 'Golos Text',sans-serif" }}>
                  {positions.length > 1 ? `Открытые позиции (${positions.length})` : "Открытая позиция"}
                </span>
              </div>

              {positions.length === 0 ? (
                <div style={{ padding: "24px 18px", font: "400 13px 'Golos Text',sans-serif", color: "var(--fg3)" }}>
                  Нет открытой позиции
                </div>
              ) : (
                positions.map((pos) => (
                  <OpenPositionCompactRow key={pos.trade_id} pos={pos} onNavigate={(tradeId) => navigate(`/trades?highlight=${encodeURIComponent(tradeId)}`)} />
                ))
              )}
            </section>
          )}

          <section style={{ borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "15px 18px", borderBottom: "1px solid var(--line)" }}>
              <span style={{ font: "600 14.5px 'Golos Text',sans-serif" }}>Сделки за период</span>
              <button type="button" onClick={() => navigate("/trades")} style={{ border: 0, background: "transparent", color: "var(--accent)", font: "600 12px 'Golos Text',sans-serif", cursor: "pointer", padding: 0 }}>
                все →
              </button>
            </div>
            {periodTrades.length === 0 ? (
              <div style={{ padding: "18px 16px", font: "400 12.5px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Сделок за выбранный период нет</div>
            ) : (
              <div>
                {periodTrades.map((m) => {
                  const pnl = parseFloat(m.pnl as string);
                  return (
                    <button
                      key={m.tradeId}
                      type="button"
                      onClick={() => navigate(`/trades?highlight=${encodeURIComponent(m.tradeId)}`)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "9px 16px",
                        border: 0,
                        borderBottom: "1px solid var(--line)",
                        background: "transparent",
                        color: "var(--fg)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {m.botLabel && (
                        <span
                          style={{
                            font: "700 9.5px 'JetBrains Mono',monospace",
                            padding: "2px 5px",
                            borderRadius: 5,
                            flex: "none",
                            background: "var(--surface3)",
                            color: "var(--fg3)",
                          }}
                        >
                          {m.botLabel}
                        </span>
                      )}
                      <span
                        style={{
                          font: "700 10.5px 'Golos Text',sans-serif",
                          letterSpacing: ".05em",
                          padding: "2px 6px",
                          borderRadius: 5,
                          flex: "none",
                          background: m.direction === "LONG" ? "var(--longsoft)" : "var(--shortsoft)",
                          color: m.direction === "LONG" ? "var(--long)" : "var(--short)",
                        }}
                      >
                        {m.direction}
                      </span>
                      <span title={m.tradeId} style={{ font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)", flex: "none" }}>
                        {formatTradeId(m.tradeId)}
                      </span>
                      <span style={{ font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formatTime(new Date(m.time * 1000).toISOString(), true)}
                      </span>
                      <span style={{ font: "600 12px 'JetBrains Mono',monospace", flex: "none", color: pnl > 0 ? "var(--long)" : pnl < 0 ? "var(--short)" : "var(--fg3)" }}>
                        {formatPnl(m.pnl as string)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// Compact "Открытая позиция" row — user request: the old inline card (SL/
// TP, probability bar, decision reasoning, all always visible) replaced by
// a single summary line; hovering it shows the FULL detail in the exact
// same format the Сделки page's own detail panel uses (TradeDetailPanel,
// fetched fresh via useTradeDetails since OpenPosition itself — from
// overview.py's _load_open_positions — doesn't carry fees/history/realized
// P&L, only /trades/{id} does). Own hover-state + TooltipBubble (not
// HoverTip) — HoverTip's dotted-underline/cursor:help chrome is meant for a
// short inline label, not a full-width row.
function OpenPositionCompactRow({ pos, onNavigate }: { pos: OpenPosition; onNavigate: (tradeId: string) => void }) {
  const { data: details } = useTradeDetails(pos.trade_id);
  const [hover, setHover] = useState(false);
  const pnl = parseFloat(pos.unrealized_pnl);
  return (
    <div style={{ position: "relative" }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        type="button"
        onClick={() => onNavigate(pos.trade_id)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "9px 16px",
          border: 0,
          borderBottom: "1px solid var(--line)",
          background: "transparent",
          color: "var(--fg)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {pos.bot_label && (
          <span
            title={pos.bot_instance_id}
            style={{
              font: "700 9.5px 'JetBrains Mono',monospace",
              padding: "2px 5px",
              borderRadius: 5,
              flex: "none",
              background: "var(--surface3)",
              color: "var(--fg3)",
            }}
          >
            {pos.bot_label}
          </span>
        )}
        <span
          style={{
            font: "700 10.5px 'Golos Text',sans-serif",
            letterSpacing: ".05em",
            padding: "2px 6px",
            borderRadius: 5,
            flex: "none",
            background: pos.direction === "LONG" ? "var(--longsoft)" : "var(--shortsoft)",
            color: pos.direction === "LONG" ? "var(--long)" : "var(--short)",
          }}
        >
          {pos.direction}
        </span>
        <span title={pos.trade_id} style={{ font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)", flex: "none" }}>
          {formatTradeId(pos.trade_id)}
        </span>
        <span style={{ font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {formatMoney(pos.entry_price)} → {formatMoney(pos.current_price)}
        </span>
        <span style={{ font: "600 12px 'JetBrains Mono',monospace", flex: "none", color: pnl > 0 ? "var(--long)" : pnl < 0 ? "var(--short)" : "var(--fg3)" }}>
          {formatPnlMoney(pos.unrealized_pnl)}
        </span>
      </button>
      {hover && (
        // "bottom" — this row sits near the top of the page (right under
        // the KPI card), same reasoning HoverTip's own placement comment
        // documents for AppShell's header widget.
        <TooltipBubble
          placement="bottom"
          content={
            <div style={{ width: 300, maxHeight: 420, overflowY: "auto" }}>
              {details ? (
                <TradeDetailPanel details={details} />
              ) : (
                <span style={{ font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>Загрузка…</span>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}
